import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  message,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import { ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import { paymentConfigService } from '../../services/paymentConfigService';
import { PaymentChannel, PaymentChannelSaveRequest, PaymentConfigResponse } from '../../types';

const { Title, Paragraph } = Typography;

const CHANNEL_LABELS: Record<PaymentChannel, string> = {
  WECHAT_MINIAPP: '微信小程序支付',
  WECHAT_H5: '微信 H5 支付',
  WECHAT_QR: '微信扫码支付',
  ALIPAY_MINIAPP: '支付宝小程序支付',
  ALIPAY_H5: '支付宝 H5 支付',
  H5_CASHIER: 'H5 收银台',
  YEEPAY: '易宝支付',
  YUNHUITENG: '云汇腾',
};

export const PAYMENT_CHANNEL_OPTIONS = [
  { label: CHANNEL_LABELS.WECHAT_MINIAPP, value: 'WECHAT_MINIAPP' },
  { label: CHANNEL_LABELS.YEEPAY, value: 'YEEPAY' },
];

const LegacyPaymentChannelConfig: React.FC = () => {
  const [config, setConfig] = useState<PaymentConfigResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [savingChannel, setSavingChannel] = useState(false);
  const [channelForm] = Form.useForm<PaymentChannelSaveRequest>();

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await paymentConfigService.queryConfig();
      if (res.status === 1) {
        setConfig(res.data);
        channelForm.setFieldsValue({ paymentChannel: res.data.paymentChannel });
      } else {
        message.error(res.msg || '获取支付配置失败');
      }
    } catch {
      message.error('获取支付配置失败');
    } finally {
      setLoading(false);
    }
  }, [channelForm]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleSaveChannel = async () => {
    const values = await channelForm.validateFields();

    setSavingChannel(true);
    try {
      const res = await paymentConfigService.saveChannel(values);
      if (res.status === 1) {
        message.success('支付通道保存成功');
        await loadConfig();
      } else {
        message.error(res.msg || '支付通道保存失败');
      }
    } catch {
      message.error('支付通道保存失败');
    } finally {
      setSavingChannel(false);
    }
  };

  const currentChannelLabel = config?.paymentChannel
    ? CHANNEL_LABELS[config.paymentChannel] || config.paymentChannel
    : '-';

  return (
    <Spin spinning={loading}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Alert
          type="warning"
          showIcon
          message="老通道全局配置"
          description="此页面用于维护旧版全局支付通道开关，仅影响仍走 legacy 支付链路的客户端。新订单请优先在「平台支付路由」中配置。"
        />

        <Card>
          <Row justify="space-between" align="middle">
            <Col>
              <Space align="center">
                <Title level={3} style={{ margin: 0 }}>
                  支付通道配置
                </Title>
                <Tag color="orange">老通道</Tag>
              </Space>
              <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
                全局支付通道会影响所有仍依赖旧版配置的付费订单，请谨慎切换。
              </Paragraph>
            </Col>
            <Col>
              <Button icon={<ReloadOutlined />} onClick={loadConfig} loading={loading}>
                刷新
              </Button>
            </Col>
          </Row>

          <Descriptions bordered column={2} style={{ marginTop: 16 }}>
            <Descriptions.Item label="当前支付通道">
              {config ? (
                <Tag color={config.paymentChannel === 'YEEPAY' ? 'blue' : 'green'}>{currentChannelLabel}</Tag>
              ) : (
                '-'
              )}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Row gutter={16}>
          <Col xs={24} lg={10}>
            <Card title="切换支付通道">
              <Form form={channelForm} layout="vertical">
                <Form.Item
                  name="paymentChannel"
                  label="支付通道"
                  rules={[{ required: true, message: '请选择支付通道' }]}
                >
                  <Select options={PAYMENT_CHANNEL_OPTIONS} />
                </Form.Item>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={handleSaveChannel}
                  loading={savingChannel}
                >
                  保存支付通道
                </Button>
              </Form>
            </Card>
          </Col>
        </Row>
      </Space>
    </Spin>
  );
};

export default LegacyPaymentChannelConfig;
